import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck, faTimes, faCheckCircle, faTimesCircle,
  faMapMarkerAlt, faBolt, faCreditCard, faFileAlt,
  faCalendarAlt, faGavel, faExclamationTriangle, faCamera
} from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../services/api';
import './ResultadosCard.css';

function ResultadosCard({ resultados }) {
  const getTotalMultas = () => {
    return resultados.resultados.reduce((total, r) => {
      if (r.success && r.data) {
        return total + (r.data.totalInfracciones || 0);
      }
      return total;
    }, 0);
  };

  const formatTimestamp = (ts) => {
    if (!ts) return null;
    if (typeof ts === 'string') return ts;
    try {
      if (ts > 1000000000000) {
        return new Date(ts).toLocaleDateString('es-AR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return new Date(ts * 1000).toLocaleDateString('es-AR');
    } catch {
      return String(ts);
    }
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('es-AR', { minimumFractionDigits: 2 });
  };

  // Construye URL completa para links relativos del backend
  const buildUrl = (link) => {
    if (!link) return null;
    if (link.startsWith('/api/')) {
      return `${API_URL}${link}`;
    }
    return link;
  };

  // Renderiza infraccion de GBA
  const renderGBAInfraccion = (inf, i) => (
    <div key={i} className="infraccion-card">
      <div className="infraccion-header">
        <span className="acta-numero">Acta: {inf.nroActa}</span>
        <span className={`estado-badge ${inf.estadoCausaPublico?.descripcion?.toLowerCase().includes('paga') ? 'pagado' : ''}`}>
          {inf.estadoCausaPublico?.descripcion || inf.estado || 'N/A'}
        </span>
      </div>

      <div className="infraccion-descripcion">
        {inf.descripcion || 'Sin descripcion'}
      </div>

      {inf.lugar && (
        <div className="infraccion-lugar">
          <FontAwesomeIcon icon={faMapMarkerAlt} /> {inf.lugar}
        </div>
      )}

      <div className="infraccion-detalles">
        <div className="detalle-row">
          <span className="detalle-label"><FontAwesomeIcon icon={faGavel} /> Autoridad:</span>
          <span className="detalle-value">{inf.autoridadAplicacion || 'N/A'}</span>
        </div>
        <div className="detalle-row">
          <span className="detalle-label"><FontAwesomeIcon icon={faCalendarAlt} /> Fecha Acta:</span>
          <span className="detalle-value">{formatTimestamp(inf.fechaActa) || 'N/A'}</span>
        </div>
        <div className="detalle-row">
          <span className="detalle-label"><FontAwesomeIcon icon={faCalendarAlt} /> Vencimiento:</span>
          <span className="detalle-value">{formatTimestamp(inf.fechaVencimiento) || 'N/A'}</span>
        </div>
      </div>

      <div className="infraccion-footer">
        <span className="monto">${formatCurrency(inf.importeTotal)}</span>
        <div className="acciones">
          {inf.sePuedePagarOnline && inf.linkPago && (
            <a href={buildUrl(inf.linkPago)} target="_blank" rel="noopener noreferrer" className="btn-accion pago">
              <FontAwesomeIcon icon={faCreditCard} /> Pagar
            </a>
          )}
          {inf.nroCausa && (
            <a href={`${API_URL}/api/v1/gba/acta/${inf.nroCausa}`} target="_blank" rel="noopener noreferrer" className="btn-accion acta">
              <FontAwesomeIcon icon={faFileAlt} /> Ver Acta
            </a>
          )}
          {inf.nroCausa && (
            <a href={`${API_URL}/api/v1/gba/prueba-fotografica/${inf.nroCausa}`} target="_blank" rel="noopener noreferrer" className="btn-accion foto">
              <FontAwesomeIcon icon={faCamera} /> Prueba Foto
            </a>
          )}
          {inf.sePuedePagarOnline && !inf.linkPago && (
            <span className="badge-info pago-online">Pago Online Disponible</span>
          )}
          {inf.sePuedeImprimir && !inf.linkActa && (
            <span className="badge-info imprimible">Imprimible</span>
          )}
        </div>
      </div>
    </div>
  );

  // Renderiza infraccion de CABA
  const renderCABAInfraccion = (inf, i) => (
    <div key={i} className="infraccion-card">
      <div className="infraccion-header">
        <span className="acta-numero">Acta: {inf.numeroActa}</span>
        <span className={`estado-badge ${inf.estado?.toLowerCase().includes('paga') ? 'pagado' : ''}`}>
          {inf.estado || 'N/A'}
        </span>
      </div>

      <div className="infraccion-tipo">{inf.tipoActa}</div>

      {inf.infracciones?.map((detalle, j) => (
        <div key={j} className="infraccion-detalle-item">
          <div className="detalle-codigo">{detalle.codigo}</div>
          <div className="detalle-info">
            <p className="detalle-descripcion">{detalle.descripcion}</p>
            {detalle.lugar && (
              <p className="detalle-lugar">
                <FontAwesomeIcon icon={faMapMarkerAlt} /> {detalle.lugar}
              </p>
            )}
            {detalle.descuenta_puntos && detalle.descuenta_puntos !== "0" && (
              <p className="detalle-puntos">
                <FontAwesomeIcon icon={faExclamationTriangle} /> Descuenta {detalle.descuenta_puntos} puntos
              </p>
            )}
          </div>
        </div>
      ))}

      <div className="infraccion-detalles">
        <div className="detalle-row">
          <span className="detalle-label"><FontAwesomeIcon icon={faCalendarAlt} /> Fecha:</span>
          <span className="detalle-value">{inf.fechaActa || 'N/A'}</span>
        </div>
      </div>

      <div className="infraccion-footer">
        <span className="monto">${formatCurrency(inf.montoActa)}</span>
      </div>
    </div>
  );

  return (
    <div className="resultados-container">
      <div className="resultados-header">
        <h2>Resultados para patente: {resultados.patente}</h2>
        <p className="timestamp">
          Consultado el: {new Date(resultados.timestamp).toLocaleString('es-AR')}
        </p>
        <div className="total-multas">
          Total de infracciones encontradas: <strong>{getTotalMultas()}</strong>
        </div>
      </div>

      {resultados.resultados.map((resultado, idx) => (
        <div key={idx} className={`jurisdiccion-result ${resultado.success ? 'success' : 'error'}`}>
          <div className="jurisdiccion-header">
            <h3>{resultado.nombre_jurisdiccion}</h3>
            <span className={`status-badge ${resultado.success ? 'success' : 'error'}`}>
              {resultado.success ? <><FontAwesomeIcon icon={faCheck} /> Exitoso</> : <><FontAwesomeIcon icon={faTimes} /> Error</>}
            </span>
          </div>

          {resultado.success && resultado.data ? (
            <div className="jurisdiccion-body">
              {resultado.data.totalInfracciones === 0 ? (
                <div className="no-infracciones">
                  <FontAwesomeIcon icon={faCheckCircle} /> Sin infracciones registradas
                </div>
              ) : (
                <>
                  <div className="resumen">
                    <div className="resumen-item">
                      <span className="label">Total Infracciones:</span>
                      <span className="value">{resultado.data.totalInfracciones}</span>
                    </div>
                    {resultado.data.montoTotal > 0 && (
                      <div className="resumen-item">
                        <span className="label">Monto Total:</span>
                        <span className="value monto">${formatCurrency(resultado.data.montoTotal)}</span>
                      </div>
                    )}
                  </div>

                  <div className="infracciones-list">
                    {resultado.data.infracciones?.map((infraccion, i) => (
                      // Detectar si es GBA o CABA por la estructura
                      infraccion.nroActa
                        ? renderGBAInfraccion(infraccion, i)
                        : renderCABAInfraccion(infraccion, i)
                    ))}
                  </div>
                </>
              )}

              {resultado.cached_token && (
                <div className="cache-notice">
                  <FontAwesomeIcon icon={faBolt} /> Consulta realizada con token cacheado (mas rapido)
                </div>
              )}
            </div>
          ) : (
            <div className="error-content">
              <FontAwesomeIcon icon={faTimesCircle} /> {resultado.error || 'Error desconocido'}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default ResultadosCard;
